import { useState, useTransition } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { ChevronDown, Globe } from 'lucide-react';
import { cn } from '@/lib/utils';
import { locales, localeNames, type Locale } from '@/i18n/config';

export function LanguageSelector() {
  const { i18n } = useTranslation();
  const locale = i18n.language as Locale;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [isPending, startTransition] = useTransition();
  const [isOpen, setIsOpen] = useState(false);

  const handleLocaleChange = (newLocale: Locale) => {
    const newPath = pathname.replace(
      new RegExp(`^/(${locales.join('|')})`),
      `/${newLocale}`,
    );
    i18n.changeLanguage(newLocale);
    startTransition(() => {
      navigate(newPath, { replace: true });
    });
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isPending}
        className={cn(
          'flex items-center gap-1 sm:gap-2 rounded-lg border border-[#E5E7EB] px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-[#313131] transition-colors hover:bg-[#F7F7F7]',
          isPending && 'opacity-50'
        )}
        style={{ backgroundColor: '#ffffff' }}
      >
        <Globe className="h-4 w-4 text-[#FF8189] flex-shrink-0" />
        <span className="hidden sm:inline">{localeNames[locale]}</span>
        <span className="sm:hidden">{locale.toUpperCase()}</span>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-[#535353] transition-transform flex-shrink-0',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div
            className="absolute right-0 top-full z-20 mt-1 w-40 rounded-lg border border-[#E5E7EB] shadow-lg overflow-hidden"
            style={{ backgroundColor: '#ffffff' }}
          >
            {locales.map((loc) => (
              <button
                key={loc}
                onClick={() => handleLocaleChange(loc)}
                className={cn(
                  'flex w-full items-center gap-2 px-4 py-3 text-left text-sm transition-colors',
                  locale === loc
                    ? 'bg-[#FF8189]/10 text-[#FF8189]'
                    : 'text-[#313131] hover:bg-[#F7F7F7]'
                )}
                style={{ backgroundColor: locale === loc ? 'rgba(255, 129, 137, 0.05)' : '#ffffff' }}
              >
                <Globe className="h-4 w-4 flex-shrink-0" />
                {localeNames[loc]}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
